import { ConnectProto, isFieldSet } from "@racoon/api";
import { fromTimestamp } from "@racoon/lib";
import { plannedContainerCount } from "~/shared/lib/job-containers";

const S = ConnectProto.JobStatus;

export const BUCKETS = ["unassigned", "planned", "done", "missed"] as const;
export type BucketKey = (typeof BUCKETS)[number];

type Bucket = { sites: number; volume: number };

export type PickupStatus = Record<BucketKey, Bucket> & {
  total: Bucket;
  doneWeightT: number;
};

export const DEFAULT_DEADLINE_MS = 24 * 60 * 60 * 1000;

/** Responses arrive with bigint ids normalized to strings, so an unset id is "0", not 0n. */
export const isSetId = (id: bigint | string | number | null | undefined) =>
  id != null && String(id) !== "0";

// tw_end_at is the deadline; the rare job without one is given a day from its creation.
export function jobDeadline(job: ConnectProto.Job): Date | null {
  const twEnd = fromTimestamp(job.twEndAt);
  if (twEnd) return twEnd;
  const created = fromTimestamp(job.createdAt);
  return created ? new Date(created.getTime() + DEFAULT_DEADLINE_MS) : null;
}

/** Whether the deadline, not the plan, dates the job: a missed job with a plan is on its plan's day. */
export const isDatedByDeadline = (job: ConnectProto.Job) =>
  job.status !== S.NOT_COMPLETED || !fromTimestamp(job.planStartAt);

/** By status alone: a job is missed only once the backend marks it so, however late it runs. */
export function bucketOf(job: ConnectProto.Job): BucketKey | null {
  switch (job.status) {
    case S.CANCELLED:
      return null;
    case S.NOT_COMPLETED:
      return "missed";
    case S.COMPLETED:
    case S.FORCE_COMPLETED:
      return "done";
  }
  if (job.status === S.CREATED || !isSetId(job.routeId)) return "unassigned";
  return "planned";
}

// The deprecated job.containerType carries no count, so it stands for a single container.
export function jobCapacity(job: ConnectProto.Job): number {
  const group = job.containerGroup;
  if (group?.containerType) return group.containerType.volume * (plannedContainerCount(job) || 1);
  return job.containerType?.volume ?? 0;
}

// fact_total_volume is unset until the containers' fill is declared; a declared 0 is real.
export function jobVolume(job: ConnectProto.Job, bucket: BucketKey): number {
  if (bucket === "done" && isFieldSet(job, ConnectProto.JobSchema.field.factTotalVolume)) {
    return job.factTotalVolume;
  }
  return jobCapacity(job);
}

export function summarizePickupStatus(
  jobs: ConnectProto.Job[],
  routes: ConnectProto.Route[],
): PickupStatus {
  const sites = Object.fromEntries(BUCKETS.map((b) => [b, new Set<string>()])) as Record<
    BucketKey,
    Set<string>
  >;
  const volume = Object.fromEntries(BUCKETS.map((b) => [b, 0])) as Record<BucketKey, number>;
  const doneRouteIds = new Set<string>();

  for (const job of jobs) {
    const bucket = bucketOf(job);
    if (!bucket) continue;
    if (isSetId(job.pickupLocationId)) sites[bucket].add(String(job.pickupLocationId));
    volume[bucket] += jobVolume(job, bucket);
    if (bucket === "done" && isSetId(job.routeId)) doneRouteIds.add(String(job.routeId));
  }

  // Weighbridge net is recorded per trip, so each route counts once however many jobs it served.
  const doneWeightKg = routes
    .filter((r) => doneRouteIds.has(String(r.id)))
    .reduce((sum, r) => sum + (r.scaleRecord?.nettoWeightKg ?? 0), 0);

  const result = Object.fromEntries(
    BUCKETS.map((b) => [b, { sites: sites[b].size, volume: volume[b] }]),
  ) as Record<BucketKey, Bucket>;

  return {
    ...result,
    total: BUCKETS.reduce(
      (acc, b) => ({ sites: acc.sites + result[b].sites, volume: acc.volume + result[b].volume }),
      { sites: 0, volume: 0 },
    ),
    doneWeightT: doneWeightKg / 1000,
  };
}
